import { BuildingOfficeIcon, UserGroupIcon } from "@heroicons/react/24/outline";
import Modal from "@via/components/modal.tsx";
import { useAuth } from "@via/hooks/useAuth.tsx";
import { useGlobal } from "@via/hooks/useGlobal.tsx";
import UpdateOrganizationForm from "@via/pages/organization/update-organization-form.tsx";
import UserInvitation from "@via/pages/organization/user-invitation.tsx";
import { ModalSize } from "@via/utils/constants.tsx";
import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { organizationMembershipFor } from "@via/hooks/organization-access.ts";

export const ModalTitle: React.FC<{ modalView: OrganizationModalView }> = ({
  modalView,
}) => {
  return modalView === OrganizationModalView.OrganizationUpdateForm ? (
    <div className="flex items-center gap-2 text-xl font-semibold">
      <BuildingOfficeIcon className="w-7 h-7" />
      <h2>Define your organization</h2>
    </div>
  ) : modalView === OrganizationModalView.UserInvitation ? (
    <div className="flex items-center gap-2 text-xl font-semibold">
      <UserGroupIcon className="w-7 h-7" />
      <h2>Science is all about collaboration</h2>
    </div>
  ) : (
    <div className="flex items-center gap-2 text-xl font-semibold">
      <BuildingOfficeIcon className="w-7 h-7" />
      <h2>Organization</h2>
    </div>
  );
};

enum OrganizationModalView {
  OrganizationUpdateForm = 1,
  UserInvitation = 2,
}

interface OrganizationModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

const OrganizationModal: React.FC<OrganizationModalProps> = ({
  isOpen,
  setIsOpen,
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { setShowOrganizationCreateModal } = useGlobal();
  const { organizationId = "" } = useParams();
  const membership = organizationMembershipFor(user, organizationId);
  const onboardingStep = user?.onboardingStep;

  const [componentView, setComponentView] = useState<OrganizationModalView>(
    OrganizationModalView.OrganizationUpdateForm
  );

  const handleOrganizationUpdateSuccess = () => {
    setComponentView(OrganizationModalView.UserInvitation);
  };

  const handleModalClose = () => {
    setShowOrganizationCreateModal(false);
    navigate({
      pathname: "/organization",
    });
  };

  const handleDoneClick = () => {
    handleModalClose();
  };

  return (
    <Modal
      size={ModalSize.Large}
      width={800}
      open={isOpen}
      setOpen={setIsOpen}
      title={<ModalTitle modalView={componentView} />}
      showButtons={false}
      onCancel={handleModalClose}
      disableClose={
        componentView === OrganizationModalView.OrganizationUpdateForm
      }
      bgOpacity={100}>
      <div className="pt-5 h-full">
        {componentView === OrganizationModalView.OrganizationUpdateForm ? (
          <UpdateOrganizationForm
            organizationId={organizationId}
            onUpdateSuccess={handleOrganizationUpdateSuccess}
            onboardingStep={onboardingStep}
            defaultValues={{
              name: membership?.name || "",
              description: membership?.description || "",
            }}
          />
        ) : componentView === OrganizationModalView.UserInvitation ? (
          <UserInvitation organizationId={organizationId} />
        ) : null}
        {componentView === OrganizationModalView.UserInvitation && (
          <div className="flex justify-end mt-4">
            <button
              id="done-button"
              type="button"
              onClick={handleDoneClick}
              className="ml-4 inline-flex justify-center rounded-md bg-green-600 hover:bg-green-500 focus-visible:outline-green-600 px-3 py-2 text-sm font-semibold text-white shadow-sm  focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ">
              Done
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default OrganizationModal;
